/**
 * RecordListView — 완료된 태스크(완료 문서) 검색/필터/목록
 *
 * 통합 보드 구조: 별도 Records 보드가 아니라 Tasks 보드 안의
 * status='done' 카드들을 대상으로 검색한다.
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Trans, t } from '@lingui/macro';
import {
  Archive, Search, FolderOpen, Tag, Users,
  Calendar, ChevronRight, FileText, Loader2,
  Filter, X, MessageCircle,
} from 'lucide-react';
import { useAppUIContext } from '../../context/AppUIContext';
import { Fire } from '../../services/FireClient';
import { toast } from '../../components/ui/use-toast';
import { Post, Board } from '../../models/Board';
import {
  searchArchive, getArchiveFilterOptions,
  ArchiveFilters, ArchiveFilterOptions,
} from '../../services/ArchiveSearchService';
import { cn } from '../../lib/utils';
import { Input } from '../../components/ui/input';

interface RecordListViewProps {
  /** 완료 카드들을 검색할 Tasks 보드 */
  tasksBoard: Board | null;
  /** 카드 클릭 시 상세 화면 열기 콜백 (옵션) */
  onPostClick?: (post: Post) => void;
}

/** 날짜 포맷 */
function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('ko-KR', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

const RecordListView: React.FC<RecordListViewProps> = ({ tasksBoard, onPostClick }) => {
  const { setMessageToShow } = useAppUIContext();
  const [posts, setPosts] = useState<Post[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [loadingSource, setLoadingSource] = useState(false);

  // 필터
  const [showFilters, setShowFilters] = useState(false);
  const [filterOptions, setFilterOptions] = useState<ArchiveFilterOptions>({
    categories: [], tags: [], participants: [],
  });
  const [activeCategory, setActiveCategory] = useState<string | undefined>();
  const [activeTags, setActiveTags] = useState<string[]>([]);
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');

  // debounce 타이머
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 필터 옵션 로드
  useEffect(() => {
    if (!tasksBoard) return;
    getArchiveFilterOptions(tasksBoard.id)
      .then(setFilterOptions)
      .catch(err => console.error('[RecordListView] 필터 옵션 로드 실패:', err));
  }, [tasksBoard]);

  // 검색 실행
  const executeSearch = useCallback(async (query: string, filters: ArchiveFilters) => {
    if (!tasksBoard) return;
    setLoading(true);
    try {
      const result = await searchArchive(tasksBoard.id, query, filters);
      setPosts(result.posts);
      setTotal(result.total);
    } catch (err) {
      console.error('[RecordListView] 검색 실패:', err);
    } finally {
      setLoading(false);
    }
  }, [tasksBoard]);

  // 현재 필터 조합
  const buildFilters = useCallback((): ArchiveFilters => ({
    category: activeCategory,
    tags: activeTags.length > 0 ? activeTags : undefined,
    dateRange: dateStart || dateEnd
      ? { start: dateStart, end: dateEnd }
      : undefined,
  }), [activeCategory, activeTags, dateStart, dateEnd]);

  // 초기 로드 + 필터 변경 시 재검색
  useEffect(() => {
    executeSearch(searchQuery, buildFilters());
  }, [tasksBoard, activeCategory, activeTags, dateStart, dateEnd]);

  // 검색어 debounce
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      executeSearch(value, buildFilters());
    }, 300);
  };

  // 태그 토글
  const toggleTag = (tag: string) => {
    setActiveTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  // 필터 초기화
  const clearAllFilters = () => {
    setActiveCategory(undefined);
    setActiveTags([]);
    setDateStart('');
    setDateEnd('');
  };

  const hasActiveFilters = activeCategory || activeTags.length > 0 || dateStart || dateEnd;

  const handleCardClick = (post: Post) => {
    if (onPostClick) {
      onPostClick(post);
      return;
    }
    setSelectedPost(selectedPost?.id === post.id ? null : post);
  };

  // 원본 쓰레드 모달로 표시 (원본 태스크 metadata.source_thread_id 사용)
  const handleGoToSource = async (post: Post) => {
    const threadId = post.metadata?.source_thread_id;
    if (!threadId) {
      toast({ title: t`No source thread linked` });
      return;
    }
    setLoadingSource(true);
    try {
      const message = await Fire.msg.getMessage(threadId);
      if (message) {
        setMessageToShow(message);
      } else {
        toast({ title: t`Original message not found`, variant: 'destructive' });
      }
    } catch (err) {
      console.error('[RecordListView] 원본 메시지 로드 실패:', err);
      toast({ title: t`Failed to load original message`, variant: 'destructive' });
    } finally {
      setLoadingSource(false);
    }
  };

  if (!tasksBoard) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
        <Trans>Tasks board is not available yet.</Trans>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* ── 검색 바 + 필터 토글 ── */}
      <div className="px-3 py-2 shrink-0 space-y-2">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
            <Input
              type="text"
              value={searchQuery}
              onChange={e => handleSearchChange(e.target.value)}
              placeholder={t`Search completed tasks...`}
              className="pl-8 h-8 text-sm"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={cn(
              'flex items-center gap-1 h-8 px-2.5 rounded-md text-xs font-medium transition-colors cursor-pointer border',
              showFilters || hasActiveFilters
                ? 'border-primary bg-primary/8 text-primary'
                : 'border-border text-muted-foreground hover:border-primary/40',
            )}
          >
            <Filter className="size-3.5" />
            <Trans>Filter</Trans>
            {hasActiveFilters && (
              <span className="size-4 rounded-full bg-primary text-background text-micro font-bold flex items-center justify-center">
                {(activeCategory ? 1 : 0) + activeTags.length + (dateStart || dateEnd ? 1 : 0)}
              </span>
            )}
          </button>
        </div>

        {/* ── 필터 패널 ── */}
        {showFilters && (
          <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-3 animate-in fade-in slide-in-from-top-1 duration-200">
            {/* 카테고리 */}
            {filterOptions.categories.length > 0 && (
              <div>
                <span className="text-meta-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1 mb-1.5">
                  <FolderOpen className="size-3" />
                  <Trans>Category</Trans>
                </span>
                <div className="flex flex-wrap gap-1">
                  {filterOptions.categories.map(cat => (
                    <button
                      key={cat}
                      onClick={() => setActiveCategory(activeCategory === cat ? undefined : cat)}
                      className={cn(
                        'px-2 py-0.5 rounded-full text-meta-sm font-medium transition-colors cursor-pointer border',
                        activeCategory === cat
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border text-muted-foreground hover:border-primary/30',
                      )}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* 태그 */}
            {filterOptions.tags.length > 0 && (
              <div>
                <span className="text-meta-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1 mb-1.5">
                  <Tag className="size-3" />
                  <Trans>Tags</Trans>
                </span>
                <div className="flex flex-wrap gap-1">
                  {filterOptions.tags.map(tag => (
                    <button
                      key={tag}
                      onClick={() => toggleTag(tag)}
                      className={cn(
                        'px-2 py-0.5 rounded-full text-meta-sm font-medium transition-colors cursor-pointer border',
                        activeTags.includes(tag)
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border text-muted-foreground hover:border-primary/30',
                      )}
                    >
                      #{tag}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* 날짜 범위 */}
            <div>
              <span className="text-meta-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1 mb-1.5">
                <Calendar className="size-3" />
                <Trans>Date Range</Trans>
              </span>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={dateStart}
                  onChange={e => setDateStart(e.target.value)}
                  className="h-7 rounded border border-border bg-background px-2 text-meta-sm text-foreground outline-none focus:ring-1 focus:ring-ring flex-1"
                />
                <span className="text-xs text-muted-foreground">—</span>
                <input
                  type="date"
                  value={dateEnd}
                  onChange={e => setDateEnd(e.target.value)}
                  className="h-7 rounded border border-border bg-background px-2 text-meta-sm text-foreground outline-none focus:ring-1 focus:ring-ring flex-1"
                />
              </div>
            </div>

            {/* 필터 초기화 */}
            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                className="flex items-center gap-1 text-meta-sm text-destructive hover:text-destructive/80 cursor-pointer"
              >
                <X className="size-3" />
                <Trans>Clear all filters</Trans>
              </button>
            )}
          </div>
        )}

        {/* 결과 카운트 */}
        {(searchQuery || hasActiveFilters) && (
          <div className="text-meta-sm text-muted-foreground px-1">
            <Trans>{total} results found</Trans>
          </div>
        )}
      </div>

      {/* ── 목록 ── */}
      <div className="flex-1 overflow-y-auto px-3 pb-3">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        ) : posts.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-muted-foreground">
            <Archive className="size-10 opacity-30" />
            <p className="text-sm">
              {searchQuery || hasActiveFilters ? (
                <Trans>No matching completed tasks</Trans>
              ) : (
                <Trans>No completed tasks yet</Trans>
              )}
            </p>
            {!searchQuery && !hasActiveFilters && (
              <p className="text-xs text-muted-foreground/60 text-center max-w-[240px]">
                <Trans>Complete a task with "Save Completion Document" to see it here.</Trans>
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {posts.map(post => {
              const completion = post.metadata?.completion ?? {};
              const sourceThreadId = post.metadata?.source_thread_id;
              return (
                <div
                  key={post.id}
                  onClick={() => handleCardClick(post)}
                  className={cn(
                    'w-full text-left rounded-lg border p-3 transition-all cursor-pointer',
                    'hover:border-primary/30 hover:shadow-sm',
                    selectedPost?.id === post.id
                      ? 'border-primary/40 bg-primary/3 shadow-sm'
                      : 'border-border bg-background',
                  )}
                >
                  {/* 제목 + 카테고리 */}
                  <div className="flex items-start gap-2">
                    <FileText className="size-4 text-muted-foreground shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-foreground truncate">
                        {post.title}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        {completion.category && (
                          <span className="inline-flex items-center gap-0.5 text-meta-sm text-primary/70 font-medium">
                            <FolderOpen className="size-2.5" />
                            {completion.category}
                          </span>
                        )}
                        <span className="text-meta-sm text-muted-foreground">
                          {formatDate(post.created_at)}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className={cn(
                      'size-4 text-muted-foreground shrink-0 transition-transform',
                      selectedPost?.id === post.id && 'rotate-90',
                    )} />
                  </div>

                  {/* 태그 */}
                  {post.tags && post.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2 pl-6">
                      {post.tags.map(tag => (
                        <span key={tag} className="px-1.5 py-0.5 rounded-full text-meta-sm bg-primary/8 text-primary/70 font-medium">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* 확장 상세 (인라인 미리보기 — onPostClick 미사용 시) */}
                  {selectedPost?.id === post.id && (
                    <div className="mt-3 pl-6 space-y-2 animate-in fade-in slide-in-from-top-1 duration-200">
                      {post.plain_text && (
                        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-4 whitespace-pre-wrap">
                          {post.plain_text}
                        </p>
                      )}

                      {completion.outcome && (
                        <div className="flex items-start gap-1.5 text-xs">
                          <span className="text-emerald-500 font-medium shrink-0">
                            <Trans>Outcome</Trans>:
                          </span>
                          <span className="text-foreground">{completion.outcome}</span>
                        </div>
                      )}

                      {completion.key_decisions?.length > 0 && (
                        <div className="space-y-0.5">
                          <span className="text-meta-sm font-medium text-muted-foreground">
                            <Trans>Key Decisions</Trans>
                          </span>
                          {completion.key_decisions.map((d: string, i: number) => (
                            <div key={i} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                              <span className="mt-1 size-1 rounded-full bg-primary/30 shrink-0" />
                              {d}
                            </div>
                          ))}
                        </div>
                      )}

                      {completion.participants?.length > 0 && (
                        <div className="flex items-center gap-1.5 text-meta-sm text-muted-foreground">
                          <Users className="size-3" />
                          {completion.participants.map((p: any) => p.name).join(', ')}
                        </div>
                      )}

                      {completion.period_start && completion.period_end && (
                        <div className="flex items-center gap-1.5 text-meta-sm text-muted-foreground">
                          <Calendar className="size-3" />
                          {formatDate(completion.period_start)} — {formatDate(completion.period_end)}
                        </div>
                      )}

                      {sourceThreadId && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleGoToSource(post); }}
                          disabled={loadingSource}
                          className="flex items-center gap-1 text-meta-sm text-primary hover:underline cursor-pointer mt-1 disabled:opacity-50"
                        >
                          {loadingSource
                            ? <Loader2 className="size-3 animate-spin" />
                            : <MessageCircle className="size-3" />
                          }
                          <Trans>View original thread</Trans>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecordListView;
